/**
 * React 19+ Access Management Component
 * 
 * Handles device access control, user permissions, access requests, and device sharing.
 * Optimized for Firebase v11+ SDK with world-class React patterns.
 * 
 * Performance Optimizations:
 * - useCallback for event handlers to prevent re-renders
 * - useMemo for expensive computations and derived state
 * - Conditional data loading based on user permissions
 * - Batched inviter info lookups to reduce API calls
 * - Firestore server timestamp handling
 * - Consolidated permission badge styles
 * - Simplified alert icon mappings
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useDevicePermission } from '../hooks/useDevicePermission.js'
import { useAccessManagement } from '../hooks/useAccessManagement.js'
import { useAccessData } from '../hooks/useAccessData.js'
import { useAuth } from '../hooks/useAuth.js'
import LoadingSpinner from '../components/LoadingSpinner.jsx'
import ModalPortal from '../components/ModalPortal.jsx'
import ConfirmationModal from '../components/ConfirmationModal.jsx'

/**
 * Access Management Component - handles device access control and user management
 */
function AccessManagement() {
  const accessRequestsSectionRef = useRef(null)

  // Consolidated state management
  const [state, setState] = useState(() => ({
    activeTab: 'users',
    inviteEmail: '',
    inviteRole: 'user',
    requestPermissions: {},
    feedback: { message: '', type: '', show: false },
    resetModal: { isOpen: false, reason: '', isLoading: false },
    confirmModal: { isOpen: false, type: null, isLoading: false, data: null },
  }))

  // Memoized feedback utilities to prevent re-renders
  const feedbackUtils = useMemo(() => {
    const alertIcons = {
      success: 'bi-check-circle-fill',
      error: 'bi-exclamation-triangle-fill', 
      warning: 'bi-exclamation-triangle-fill',
      info: 'bi-info-circle-fill',
    }

    return {
      show: (message, type = 'info', duration = 5000) => {
        setState(prev => ({
          ...prev,
          feedback: { message, type, show: true, timestamp: Date.now() },
        }))
        
        if (duration > 0) {
          setTimeout(() => {
            setState(prev => ({ ...prev, feedback: { ...prev.feedback, show: false } }))
          }, duration)
        }
      },
      hide: () => setState(prev => ({ ...prev, feedback: { ...prev.feedback, show: false } })),
      getAlertClass: (type) => {
        const classes = {
          success: 'alert-success',
          error: 'alert-danger',
          warning: 'alert-warning',
        }
        return classes[type] || 'alert-info'
      },
      getAlertIcon: (type) => alertIcons[type] || alertIcons.info,
    }
  }, [])

  // Context and hooks
  const { 
    selectedDeviceId, 
    devicesLoading,
    navigationContext,
    clearNavigationContext,
  } = useCloudContext()
  
  const { user: currentUser } = useAuth()
  
  const { 
    permission: userPermission,
    canManage,
    display: permissionDisplay,
    style: permissionStyle,
    permissionData,
  } = useDevicePermission(selectedDeviceId)

  const { 
    removeAccess,
    changePermission,
    approveAccessRequest,
    denyAccessRequest,
    inviteUser,
    revokeInvitation,
    resetDevice,
    leaveDevice,
    loading: accessLoading,
    error: accessError,
  } = useAccessManagement()

  const { useDeviceAccessRequests, useDeviceUsers, useDeviceInvitations, getUserInfo } = useAccessData()
  
  // Permission helpers
  const isAdmin = useMemo(() => 
    userPermission === 'admin' || userPermission === 'sysadmin', 
    [userPermission],
  )

  const {
    data: accessRequests,
    isLoading: accessRequestsLoading,
  } = useDeviceAccessRequests(isAdmin ? selectedDeviceId : null)
  
  const {
    data: deviceUsers,
    isLoading: deviceUsersLoading,
    refresh: refreshDeviceUsers,
  } = useDeviceUsers(userPermission ? selectedDeviceId : null)
  
  const {
    data: deviceInvitations,
    isLoading: deviceInvitationsLoading,
  } = useDeviceInvitations(isAdmin ? selectedDeviceId : null)
  
  const [deviceInviterMap, setDeviceInviterMap] = useState({})
  
  // Optimized user lookup for device inviter information
  useEffect(() => {
    if (!deviceInvitations?.length) return

    const pendingInvitations = deviceInvitations.filter(inv => 
      inv.invitedBy && !deviceInviterMap[inv.id],
    )

    if (pendingInvitations.length === 0) return

    // Batch process inviter lookups to reduce re-renders
    Promise.all(
      pendingInvitations.map(async (inv) => {
        const user = await getUserInfo(inv.invitedBy)
        return {
          id: inv.id,
          name: user?.displayName || user?.name || user?.email || inv.invitedBy,
        }
      }),
    ).then(inviterData => {
      const newInviterMap = {}
      inviterData.forEach(({ id, name }) => {
        newInviterMap[id] = name
      })
      
      setDeviceInviterMap(prev => ({ ...prev, ...newInviterMap }))
    }).catch(console.error)
  }, [deviceInvitations, getUserInfo, deviceInviterMap])
  
  const accessDataLoading = accessRequestsLoading || deviceUsersLoading || deviceInvitationsLoading

  // Utility functions - optimized for Firebase Firestore timestamps
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return 'Never'
    // Handle Firestore Timestamp objects and regular Date objects
    const date = timestamp?.toDate?.() ?? new Date(timestamp)
    return date.toLocaleString()
  }, [])

  const formatAddedBy = useCallback((deviceUser) => {
    if (!deviceUser?.addedByInfo) return null
    if (deviceUser.userId === deviceUser.addedBy) return 'Self'
    return deviceUser.addedByInfo.displayName || deviceUser.addedByInfo.name || deviceUser.addedByInfo.email || 'Unknown'
  }, [])

  // Event handlers
  const handleTabChange = useCallback((tabId) => {
    setState(prev => ({ ...prev, activeTab: tabId }))
  }, [])

  const handleInviteInputChange = useCallback((field) => (e) => {
    setState(prev => ({ ...prev, [field]: e.target.value }))
  }, [])

  const handleRequestPermissionChange = useCallback((requestId, permission) => {
    setState(prev => ({
      ...prev,
      requestPermissions: { ...prev.requestPermissions, [requestId]: permission },
    }))
  }, [])

  const handleInviteUser = useCallback(async () => {
    if (!state.inviteEmail.trim()) {
      feedbackUtils.show('Please enter an email address', 'error')
      return
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(state.inviteEmail.trim())) {
      feedbackUtils.show('Please enter a valid email address', 'error')
      return
    }
    
    try {
      const success = await inviteUser(selectedDeviceId, state.inviteEmail.trim(), state.inviteRole)
      if (success) {
        setState(prev => ({
          ...prev,
          activeTab: 'users',
          inviteEmail: '',
          inviteRole: 'user',
        }))
        feedbackUtils.show(`Invitation sent successfully to ${state.inviteEmail}`, 'success')
      }
    } catch (error) {
      feedbackUtils.show(`Failed to send invitation: ${error.message || 'Unknown error'}`, 'error')
    }
  }, [selectedDeviceId, state.inviteEmail, state.inviteRole, inviteUser, feedbackUtils])

  const handleApproveAccess = useCallback(async (requestId, grantedPermission) => {
    try {
      await approveAccessRequest(requestId, grantedPermission)
      feedbackUtils.show(`Access request approved with ${grantedPermission} permissions`, 'success')
    } catch (error) {
      feedbackUtils.show(`Failed to approve access request: ${error.message || 'Unknown error'}`, 'error')
    }
  }, [approveAccessRequest, feedbackUtils])

  const handleDenyAccess = useCallback(async (requestId) => {
    try {
      await denyAccessRequest(requestId)
      feedbackUtils.show('Access request denied', 'warning')
    } catch (error) {
      feedbackUtils.show(`Failed to deny access request: ${error.message || 'Unknown error'}`, 'error')
    }
  }, [denyAccessRequest, feedbackUtils])

  const handleRemoveUser = useCallback((userId) => {
    const userToRemove = deviceUsers?.find(user => user.userId === userId)
    if (!userToRemove) return

    const userName = userToRemove.userInfo?.displayName || userToRemove.userInfo?.name || userToRemove.userInfo?.email || 'User'
    const isAdmin = userToRemove.permissions === 'admin'
    const isLastAdmin = isAdmin && deviceUsers.filter(u => u.permissions === 'admin').length === 1
    const isSelf = userId === currentUser?.uid

    setState(prev => ({
      ...prev,
      confirmModal: {
        isOpen: true,
        type: isSelf ? 'leaveDevice' : 'removeUser',
        isLoading: false,
        data: {
          userId,
          userName,
          userEmail: userToRemove.userInfo?.email,
          permissions: userToRemove.permissions,
          isAdmin,
          isLastAdmin,
          isSelf,
        },
      },
    }))
  }, [deviceUsers, currentUser?.uid])

  const handleConfirmRemoveUser = useCallback(async () => {
    const { userId, userName, isAdmin } = state.confirmModal.data
    
    setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: true } }))

    try {
      await removeAccess(selectedDeviceId, userId)
      
      // Force refresh the device users list to ensure UI updates immediately
      refreshDeviceUsers()
      
      if (isAdmin) {
        feedbackUtils.show(`Admin ${userName} removed from device access`, 'warning')
      } else {
        feedbackUtils.show(`${userName} removed from device access`, 'success')
      }

      setState(prev => ({
        ...prev,
        confirmModal: { isOpen: false, type: null, isLoading: false, data: null },
      }))
    } catch (error) {
      feedbackUtils.show(`Failed to remove user: ${error.message || 'Unknown error'}`, 'error')
      setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: false } }))
    }
  }, [state.confirmModal.data, removeAccess, selectedDeviceId, feedbackUtils, refreshDeviceUsers])

  const handleConfirmLeaveDevice = useCallback(async () => {
    setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: true } }))

    try {
      await leaveDevice(selectedDeviceId)
      
      feedbackUtils.show('You have left the device. You no longer have access.', 'warning')

      setState(prev => ({
        ...prev,
        confirmModal: { isOpen: false, type: null, isLoading: false, data: null },
      }))
    } catch (error) {
      feedbackUtils.show(`Failed to leave device: ${error.message || 'Unknown error'}`, 'error')
      setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: false } }))
    }
  }, [leaveDevice, selectedDeviceId, feedbackUtils])

  const handleDirectPermissionChange = useCallback(async (userId, newPermission) => {
    try {
      const userToChange = deviceUsers?.find(user => user.userId === userId)
      const userName = userToChange?.userInfo?.displayName || userToChange?.userInfo?.name || userToChange?.userInfo?.email || 'User'
      const oldPermission = userToChange?.permissions
      
      await changePermission(selectedDeviceId, userId, newPermission)
      
      feedbackUtils.show(`${userName}'s permission changed from ${oldPermission} to ${newPermission}`, 'success')
    } catch (error) {
      feedbackUtils.show(`Failed to change permission: ${error.message || 'Unknown error'}`, 'error')
    }
  }, [changePermission, selectedDeviceId, deviceUsers, feedbackUtils])

  const handleChangePermission = useCallback((userId, newPermission) => {
    const userToChange = deviceUsers?.find(user => user.userId === userId)
    if (!userToChange) return

    const userName = userToChange.userInfo?.displayName || userToChange.userInfo?.name || userToChange.userInfo?.email || 'User'
    const oldPermission = userToChange.permissions

    const isSignificantDowngrade = (
      (oldPermission === 'admin' && newPermission !== 'admin') ||
      (oldPermission === 'technician' && newPermission === 'user')
    )

    if (isSignificantDowngrade) {
      setState(prev => ({
        ...prev,
        confirmModal: {
          isOpen: true,
          type: 'changePermission',
          isLoading: false,
          data: {
            userId,
            userName,
            userEmail: userToChange.userInfo?.email,
            oldPermission,
            newPermission,
            isDowngrade: true,
          },
        },
      }))
    } else {
      handleDirectPermissionChange(userId, newPermission)
    }
  }, [deviceUsers, handleDirectPermissionChange])

  const handleConfirmChangePermission = useCallback(async () => {
    const { userId, newPermission } = state.confirmModal.data
    
    setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: true } }))

    try {
      await handleDirectPermissionChange(userId, newPermission)

      setState(prev => ({
        ...prev,
        confirmModal: { isOpen: false, type: null, isLoading: false, data: null },
      }))
    } catch (_error) {
      setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: false } }))
    }
  }, [state.confirmModal.data, handleDirectPermissionChange])

  const handleRevokeInvitation = useCallback((invitationId, invitedEmail) => {
    setState(prev => ({
      ...prev,
      confirmModal: {
        isOpen: true,
        type: 'revokeInvitation',
        isLoading: false,
        data: { invitationId, invitedEmail },
      },
    }))
  }, [])

  const handleConfirmRevokeInvitation = useCallback(async () => {
    const { invitationId, invitedEmail } = state.confirmModal.data
    
    setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: true } }))

    try {
      await revokeInvitation(invitationId)
      feedbackUtils.show(`Invitation to ${invitedEmail} has been revoked`, 'success')

      setState(prev => ({
        ...prev,
        confirmModal: { isOpen: false, type: null, isLoading: false, data: null },
      }))
    } catch (error) {
      feedbackUtils.show(`Failed to revoke invitation: ${error.message || 'Unknown error'}`, 'error')
      setState(prev => ({ ...prev, confirmModal: { ...prev.confirmModal, isLoading: false } }))
    }
  }, [state.confirmModal.data, revokeInvitation, feedbackUtils])

  const handleConfirmModalClose = useCallback(() => {
    if (state.confirmModal.isLoading) return
    setState(prev => ({
      ...prev,
      confirmModal: { isOpen: false, type: null, isLoading: false, data: null },
    }))
  }, [state.confirmModal.isLoading])

  const handleConfirmModalConfirm = useCallback(async () => {
    switch (state.confirmModal.type) {
      case 'removeUser':
        return handleConfirmRemoveUser()
      case 'revokeInvitation':
        return handleConfirmRevokeInvitation()
      case 'changePermission':
        return handleConfirmChangePermission()
      case 'leaveDevice':
        return handleConfirmLeaveDevice()
      default:
        console.warn('Unknown confirmation modal type:', state.confirmModal.type)
    }
  }, [state.confirmModal.type, handleConfirmRemoveUser, handleConfirmRevokeInvitation, handleConfirmChangePermission, handleConfirmLeaveDevice])

  // Get modal props based on confirmation type
  const getConfirmModalProps = useCallback((type, data) => {
    if (!type || !data) return {}

    const baseProps = {
      removeUser: {
        title: `Remove ${data.isAdmin ? 'Admin' : 'User'} Access`,
        message: `Remove ${data.userName} from this device?`,
        confirmText: data.isSelf ? 'Leave Device' : 'Remove User',
        confirmVariant: 'danger',
        icon: 'bi-person-dash-fill',
        iconColor: 'text-danger',
        loadingText: data.isSelf ? 'Leaving device...' : 'Removing user...',
      },
      revokeInvitation: {
        title: 'Revoke Invitation',
        message: `Cancel the invitation sent to ${data.invitedEmail}?`,
        confirmText: 'Revoke Invitation',
        confirmVariant: 'warning',
        icon: 'bi-envelope-slash-fill',
        iconColor: 'text-warning',
        loadingText: 'Revoking invitation...',
      },
      changePermission: {
        title: 'Change User Permissions',
        message: `Change ${data.userName}'s role from ${data.oldPermission} to ${data.newPermission}?`,
        confirmText: 'Change Permission',
        confirmVariant: 'warning',
        icon: 'bi-arrow-down-circle-fill',
        iconColor: 'text-warning',
        loadingText: 'Changing permissions...',
      },
      leaveDevice: {
        title: 'Leave Device',
        message: 'Are you sure you want to leave this device?',
        confirmText: 'Leave Device',
        confirmVariant: 'danger',
        icon: 'bi-box-arrow-right',
        iconColor: 'text-danger',
        loadingText: 'Leaving device...',
      },
    }

    const props = baseProps[type]
    if (!props) return {}

    // Add details based on type
    if (type === 'removeUser') {
      props.details = (
        <div>
          <div className="d-flex align-items-start">
            <i className="bi bi-info-circle-fill me-2 mt-1"></i>
            <div>
              <strong>User Details:</strong>
              <ul className="mb-2 mt-2 small">
                <li><strong>Name:</strong> {data.userName}</li>
                {data.userEmail && <li><strong>Email:</strong> {data.userEmail}</li>}
                <li><strong>Current Role:</strong> {data.permissions}</li>
              </ul>
              <strong>What will happen:</strong>
              <ul className="mb-0 mt-2 small">
                <li>{data.userName} will lose all access to this device</li>
                <li>Any pending access requests from this user will be cancelled</li>
                {data.isLastAdmin && (
                  <li className="text-danger"><strong>Warning:</strong> This will remove the last admin from the device</li>
                )}
                {data.isSelf && (
                  <li className="text-warning"><strong>Note:</strong> You will lose access and may be redirected</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )
    } else if (type === 'revokeInvitation') {
      props.details = (
        <div>
          <div className="d-flex align-items-start">
            <i className="bi bi-info-circle-fill me-2 mt-1"></i>
            <div>
              <strong>What will happen:</strong>
              <ul className="mb-0 mt-2 small">
                <li>The invitation will be cancelled</li>
                <li>{data.invitedEmail} will no longer be able to accept the invitation</li>
                <li>You can send a new invitation later if needed</li>
              </ul>
            </div>
          </div>
        </div>
      )
    } else if (type === 'changePermission') {
      props.details = (
        <div>
          <div className="d-flex align-items-start">
            <i className="bi bi-info-circle-fill me-2 mt-1"></i>
            <div>
              <strong>Permission Change:</strong>
              <ul className="mb-2 mt-2 small">
                <li><strong>User:</strong> {data.userName}</li>
                {data.userEmail && <li><strong>Email:</strong> {data.userEmail}</li>}
                <li><strong>Current Role:</strong> {data.oldPermission}</li>
                <li><strong>New Role:</strong> {data.newPermission}</li>
              </ul>
              {data.isDowngrade && (
                <>
                  <strong className="text-warning">This is a permission downgrade:</strong>
                  <ul className="mb-0 mt-2 small">
                    <li>{data.userName} will lose some privileges</li>
                    <li>They may no longer be able to manage other users</li>
                    <li>Access to admin-only features will be restricted</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )
    } else if (type === 'leaveDevice') {
      props.details = (
        <div>
          <div className="d-flex align-items-start">
            <i className="bi bi-info-circle-fill me-2 mt-1"></i>
            <div>
              <strong>What will happen:</strong>
              <ul className="mb-0 mt-2 small">
                <li>You will lose all access to this device</li>
                <li>You cannot rejoin without a new invitation</li>
                <li>You may be redirected after leaving</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }

    return props
  }, [])

  const handleResetDevice = useCallback(() => {
    setState(prev => ({
      ...prev,
      resetModal: { isOpen: true, reason: '', isLoading: false },
    }))
  }, [])

  const handleResetModalClose = useCallback(() => {
    if (state.resetModal.isLoading) return
    setState(prev => ({
      ...prev,
      resetModal: { isOpen: false, reason: '', isLoading: false },
    }))
  }, [state.resetModal.isLoading])

  const handleResetModalConfirm = useCallback(async () => {
    if (!state.resetModal.reason.trim()) {
      return
    }

    setState(prev => ({ ...prev, resetModal: { ...prev.resetModal, isLoading: true } }))

    try {
      const result = await resetDevice(selectedDeviceId, state.resetModal.reason.trim())
      
      feedbackUtils.show(
        `Device reset successfully. ${result.removedUsers?.length || 0} users removed, ` +
        `${result.cancelledRequests || 0} pending requests cancelled.`,
        'warning',
      )
      
      setState(prev => ({
        ...prev,
        resetModal: { isOpen: false, reason: '', isLoading: false },
      }))
    } catch (error) {
      feedbackUtils.show(`Failed to reset device: ${error.message || 'Unknown error'}`, 'error')
      setState(prev => ({ ...prev, resetModal: { ...prev.resetModal, isLoading: false } }))
    }
  }, [resetDevice, selectedDeviceId, feedbackUtils, state.resetModal.reason])

  // Memoized permission descriptions
  const permissionDescriptions = useMemo(() => ({
    admin: 'Full access to all settings and user management',
    technician: 'Read/write access to device settings', 
    user: 'Read-only access to device data',
    sysadmin: 'System administrator - full access',
  }), [])

  // Memoized badge styles for user permissions
  const permissionBadgeStyle = useMemo(() => {
    const styles = {
      sysadmin: 'bg-danger',
      admin: 'bg-primary',
      technician: 'bg-warning', 
      user: 'bg-success',
    }
    return styles
  }, [])

  // Memoized current user access info
  const currentUserAccessInfo = useMemo(() => {
    if (!permissionData?.addedAt) return null
    
    const addedDate = permissionData.addedAt instanceof Date 
      ? permissionData.addedAt.toLocaleDateString()
      : formatTimestamp(permissionData.addedAt)
    
    let addedByText = 'Unknown'
    if (permissionData.addedBy) {
      const currentUserEntry = deviceUsers?.find(u => u.userId === currentUser?.uid)
      addedByText = currentUserEntry ? formatAddedBy(currentUserEntry) : 'Administrator'
    }
    
    return { addedDate, addedByText }
  }, [permissionData, deviceUsers, currentUser?.uid, formatAddedBy, formatTimestamp])

  // Handle navigation context from notifications
  useEffect(() => {
    if (navigationContext !== 'notification-access-request') return
    
    setState(prev => ({ ...prev, activeTab: 'requests' }))
    
    // Scroll to access requests section after state update
    const timeoutId = setTimeout(() => {
      accessRequestsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
    
    clearNavigationContext()
    
    return () => clearTimeout(timeoutId)
  }, [navigationContext, clearNavigationContext])

  // Early returns for loading and error states
  if (devicesLoading) {
    return <LoadingSpinner message="Loading access management..." />
  }

  // No device selected state
  if (!selectedDeviceId) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        Please select a device to manage access controls.
      </div>
    )
  }

  return (
    <div className="container mt-3 mb-5">
      {/* Page Heading */}
      <header className="row mb-3">
        <div className="col d-flex align-items-center justify-content-between">
          <h2 className="mb-0 fw-bold">Access Management</h2>
        </div>
      </header>

      {/* User's Current Access Level */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">
                <i className="bi bi-person-badge me-2"></i>
                Your Access Level
              </h6>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted">
                    {permissionDescriptions[userPermission] || 'No access to this device'}
                  </div>
                  
                  {currentUserAccessInfo && (
                    <div className="text-muted small mt-2">
                      <i className="bi bi-clock me-1"></i>
                      Access granted: {currentUserAccessInfo.addedDate}
                      <span className="ms-2">
                        <i className="bi bi-person me-1"></i>
                        Added by {currentUserAccessInfo.addedByText}
                      </span>
                    </div>
                  )}
                </div>
                <span className={`badge ${permissionStyle.badge}`}>
                  <i className={`bi ${permissionStyle.icon} me-1`}></i>
                  {permissionDisplay}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Access Management Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">
                <i className="bi bi-people me-2"></i>
                Device Access Control
              </h6>
            </div>
            <div className="card-body">
              {/* Access management error display */}
              {accessError && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {accessError?.userMessage || accessError?.message || 'An error occurred'}
                </div>
              )}
              
              {/* Feedback alert */}
              {state.feedback.show && (
                <div className={`alert ${feedbackUtils.getAlertClass(state.feedback.type)} alert-dismissible fade show mb-3`} role="alert">
                  <div className="d-flex align-items-center">
                    <i className={`bi ${feedbackUtils.getAlertIcon(state.feedback.type)} me-2`}></i>
                    <span>{state.feedback.message}</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={feedbackUtils.hide}
                    aria-label="Close"
                  ></button>
                </div>
              )}
              
              {canManage ? (
                <div>
                  {/* Tab Navigation */}
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button 
                        className={`nav-link ${state.activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => handleTabChange('users')}
                        type="button"
                      >
                        <i className="bi bi-people me-2"></i>
                        Users
                      </button>
                    </li>
                    
                    {accessRequests?.length > 0 && (
                      <li className="nav-item" role="presentation">
                        <button 
                          className={`nav-link ${state.activeTab === 'requests' ? 'active' : ''}`}
                          onClick={() => handleTabChange('requests')}
                          type="button"
                        >
                          <i className="bi bi-person-check me-2"></i>
                          Requests
                          <span className="badge bg-primary text-white ms-2">
                            {accessRequests.length}
                          </span>
                        </button>
                      </li>
                    )}
                    
                    {deviceInvitations?.filter(inv => inv.status === 'pending').length > 0 && (
                      <li className="nav-item" role="presentation">
                        <button 
                          className={`nav-link ${state.activeTab === 'invitations' ? 'active' : ''}`}
                          onClick={() => handleTabChange('invitations')}
                          type="button"
                        >
                          <i className="bi bi-envelope me-2"></i>
                          Invitations
                          <span className="badge bg-primary text-white ms-2">
                            {deviceInvitations.filter(inv => inv.status === 'pending').length}
                          </span>
                        </button>
                      </li>
                    )}
                    
                    <li className="nav-item" role="presentation">
                      <button 
                        className={`nav-link ${state.activeTab === 'invite' ? 'active' : ''}`}
                        onClick={() => handleTabChange('invite')}
                        type="button"
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Invite User
                      </button>
                    </li>
                  </ul>
                  
                  {/* Tab Content */}
                  <div className="tab-content mt-3">
                    {/* Users Tab */}
                    {state.activeTab === 'users' && (
                      <div className="tab-pane active">
                        <h6 className="fw-semibold mb-3">
                          <i className="bi bi-people me-2"></i>
                          Device Users
                        </h6>
                        
                        {accessDataLoading ? (
                          <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            Loading device users...
                          </div>
                        ) : deviceUsers?.length > 0 ? (
                          <div className="list-group list-group-flush">
                            {deviceUsers.map((user) => (
                              <div key={user.userId || user.id} className="list-group-item px-0">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <div className="fw-medium">
                                      {user.userInfo?.displayName || user.userInfo?.name || 'Unknown User'}
                                    </div>
                                    <div className="text-muted small">{user.userInfo?.email || 'No email'}</div>
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                      <small className="text-muted">
                                        Role: <span className={`badge ${permissionBadgeStyle[user.permissions] || permissionBadgeStyle.user}`}>
                                          {user.permissions || 'user'}
                                        </span>
                                      </small>
                                      {/* Permission change dropdown - only show for other users */}
                                      {user.userId !== currentUser?.uid && (
                                        <select 
                                          className="form-select form-select-sm" 
                                          style={{ width: 'auto', minWidth: '120px' }}
                                          value={user.permissions || 'user'}
                                          onChange={(e) => handleChangePermission(user.userId, e.target.value)}
                                          disabled={accessLoading}
                                          title="Change user permission"
                                        >
                                          <option value="user">User</option>
                                          <option value="technician">Technician</option>
                                          <option value="admin">Admin</option>
                                        </select>
                                      )}
                                    </div>
                                    <small className="text-muted">
                                      {user.addedAt && (
                                        <span className="ms-2">
                                          • Added: {formatTimestamp(user.addedAt)}
                                        </span>
                                      )}
                                      {user.addedByInfo && (
                                        <span className="ms-2">
                                          • Added by: {formatAddedBy(user)}
                                        </span>
                                      )}
                                    </small>
                                  </div>
                                  {/* Allow admin removal only if there are multiple admins, or if removing non-admin users */}
                                  {(user.permissions !== 'admin' || (user.permissions === 'admin' && deviceUsers?.filter(u => u.permissions === 'admin').length > 1)) && (
                                    <button 
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleRemoveUser(user.userId)}
                                      disabled={accessLoading}
                                      title={user.permissions === 'admin' ? 'Remove Admin (multiple admins required)' : 'Remove User'}
                                    >
                                      <i className="bi bi-person-dash me-1"></i>
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <i className="bi bi-people" style={{ fontSize: '2rem' }}></i>
                            <div className="mt-2">No users found</div>
                          </div>
                        )}

                        {/* Device Reset Section - Admin only */}
                        {isAdmin && (
                          <div className="mt-4 pt-3 border-top">
                            <h6 className="fw-semibold text-danger mb-3">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              Danger Zone
                            </h6>
                            <div className="card border-danger">
                              <div className="card-body">
                                <h6 className="card-title text-danger">Reset Device Ownership</h6>
                                <p className="card-text text-muted small mb-3">
                                  Remove ALL users (including yourself) from this device. The device will become unclaimed 
                                  and the next user to register it will become the administrator. This action cannot be undone.
                                </p>
                                <button 
                                  className="btn btn-danger btn-sm"
                                  onClick={handleResetDevice}
                                  disabled={accessLoading}
                                  title="Reset device ownership - removes all users"
                                >
                                  <i className="bi bi-arrow-clockwise me-1"></i>
                                  Reset Device Ownership
                                </button>
                                {accessLoading && (
                                  <div className="spinner-border spinner-border-sm ms-2" role="status">
                                    <span className="visually-hidden">Processing...</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Access Requests Tab */}
                    {state.activeTab === 'requests' && (
                      <div className="tab-pane active" ref={accessRequestsSectionRef}>
                        <h6 className="fw-semibold mb-3">
                          <i className="bi bi-person-check me-2"></i>
                          Pending Access Requests
                        </h6>
                        
                        {accessDataLoading ? (
                          <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            Loading access requests...
                          </div>
                        ) : accessRequests?.length > 0 ? (
                          <div className="list-group list-group-flush">
                            {accessRequests.map((request) => {
                              const selectedPermission = state.requestPermissions[request.id] || 'user'
                              return (
                                <div key={request.id} className="list-group-item px-0">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1 me-3">
                                      <div className="fw-medium">{request.requestedByName || request.requestedByEmail}</div>
                                      <div className="text-muted small">{request.requestedByEmail}</div>
                                      <small className="text-muted">
                                        Requested: {formatTimestamp(request.createdAt)}
                                      </small>
                                      
                                      {/* Permission level selector */}
                                      <div className="mt-2">
                                        <label className="form-label small fw-medium">Grant Access Level:</label>
                                        <select 
                                          className="form-select form-select-sm"
                                          value={selectedPermission}
                                          onChange={(e) => handleRequestPermissionChange(request.id, e.target.value)}
                                          disabled={accessLoading}
                                        >
                                          <option value="user">User - View device data and basic settings</option>
                                          <option value="technician">Technician - Modify configurations and advanced settings</option>
                                          <option value="admin">Administrator - Full access including user management</option>
                                        </select>
                                      </div>
                                    </div>
                                    <div className="btn-group btn-group-sm flex-shrink-0" role="group">
                                      <button 
                                        className="btn btn-outline-success"
                                        onClick={() => handleApproveAccess(request.id, selectedPermission)}
                                        disabled={accessLoading}
                                        title={`Approve with ${selectedPermission} access`}
                                      >
                                        <i className="bi bi-check-lg me-1"></i>
                                        Approve
                                      </button>
                                      <button 
                                        className="btn btn-outline-danger"
                                        onClick={() => handleDenyAccess(request.id)}
                                        disabled={accessLoading}
                                        title="Deny Request"
                                      >
                                        <i className="bi bi-x-lg me-1"></i>
                                        Deny
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <i className="bi bi-check-circle" style={{ fontSize: '2rem' }}></i>
                            <div className="mt-2">No pending access requests</div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Invitations Tab */}
                    {state.activeTab === 'invitations' && (
                      <div className="tab-pane active">
                        <h6 className="fw-semibold mb-3">
                          <i className="bi bi-envelope me-2"></i>
                          Pending Device Invitations
                        </h6>
                        
                        {deviceInvitationsLoading ? (
                          <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            Loading invitations...
                          </div>
                        ) : deviceInvitations?.length > 0 ? (
                          <div className="list-group list-group-flush">
                            {deviceInvitations
                              .filter(invitation => invitation.status === 'pending')
                              .map((invitation) => (
                              <div key={invitation.id} className="list-group-item px-0">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1 me-3">
                                    <div className="fw-medium">{invitation.invitedEmail}</div>
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                      <small className="text-muted">
                                        Role: <span className={`badge ${permissionBadgeStyle[invitation.permission] || permissionBadgeStyle.user}`}>
                                          {invitation.permission || 'user'}
                                        </span>
                                      </small>
                                      <small className="text-muted">
                                        Status: <span className={`badge ${
                                          invitation.status === 'pending' ? 'bg-info' : 
                                          invitation.status === 'accepted' ? 'bg-success' : 
                                          invitation.status === 'revoked' ? 'bg-secondary' : 'bg-secondary'
                                        }`}>
                                          {invitation.status}
                                        </span>
                                      </small>
                                    </div>
                                    <small className="text-muted">
                                      Invited: {formatTimestamp(invitation.invitedAt || invitation.createdAt)}
                                      {invitation.invitedBy && (
                                        <span className="ms-2">
                                          • By: {deviceInviterMap[invitation.id] || invitation.invitedBy}
                                        </span>
                                      )}
                                      {invitation.expiresAt && (
                                        <span className="ms-2">
                                          • Expires: {formatTimestamp(invitation.expiresAt)}
                                        </span>
                                      )}
                                      {invitation.revokedAt && (
                                        <span className="ms-2 text-warning">
                                          • Revoked: {formatTimestamp(invitation.revokedAt)}
                                        </span>
                                      )}
                                    </small>
                                  </div>
                                  {invitation.status === 'pending' && (
                                    <button 
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleRevokeInvitation(invitation.id, invitation.invitedEmail)}
                                      disabled={accessLoading}
                                      title="Revoke Invitation"
                                    >
                                      <i className="bi bi-x-circle me-1"></i>
                                      Revoke
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <i className="bi bi-envelope-check" style={{ fontSize: '2rem' }}></i>
                            <div className="mt-2">No pending invitations</div>
                            <small>All device invitations have been processed</small>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Invite User Tab */}
                    {state.activeTab === 'invite' && (
                      <div className="tab-pane active">
                        <h6 className="fw-semibold mb-3">
                          <i className="bi bi-person-plus me-2"></i>
                          Invite User
                        </h6>
                        
                        <div className="mb-3">
                          <label className="form-label">Email Address</label>
                          <input 
                            type="email" 
                            className="form-control" 
                            value={state.inviteEmail} 
                            placeholder="user@example.com"
                            onChange={handleInviteInputChange('inviteEmail')}
                            disabled={accessLoading}
                          />
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label">Access Role</label>
                          <select 
                            className="form-select" 
                            value={state.inviteRole}
                            onChange={handleInviteInputChange('inviteRole')}
                            disabled={accessLoading}
                          >
                            <option value="user">User (Read-only)</option>
                            <option value="technician">Technician (Read/Write)</option>
                            <option value="admin">Administrator (Full Access)</option>
                          </select>
                        </div>
                        
                        <div className="d-flex gap-2">
                          <button 
                            className="btn btn-primary"
                            onClick={handleInviteUser}
                            disabled={accessLoading || !state.inviteEmail.trim()}
                          >
                            {accessLoading ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-send me-1"></i>
                                Send Invitation
                              </>
                            )}
                          </button>
                          
                          <button 
                            className="btn btn-outline-secondary"
                            onClick={() => handleTabChange('users')}
                            disabled={accessLoading}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="form-label fw-medium">Device Sharing:</label>
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-person-plus" style={{ fontSize: '2.5rem' }}></i>
                    <div className="mt-2 fw-medium">Device Sharing</div>
                    <small>Contact an admin to request access changes</small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reset Device Confirmation Modal */}
      <ModalPortal
        isOpen={state.resetModal.isOpen}
        onClose={handleResetModalClose}
        size="medium"
        closeOnBackdrop={!state.resetModal.isLoading}
        closeOnEscape={!state.resetModal.isLoading}
      >
        <div className="p-4">
          <div className="text-center mb-4">
            <div className="text-danger mb-3">
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '3rem' }}></i>
            </div>
            <h4 className="text-danger mb-2">Reset Device Ownership</h4>
            <p className="text-muted mb-0">
              This action will permanently remove <strong>ALL users</strong> (including yourself) from this device.
            </p>
          </div>

          <div className="alert alert-warning border-warning">
            <div className="d-flex align-items-start">
              <i className="bi bi-info-circle-fill text-warning me-2 mt-1"></i>
              <div>
                <strong>What will happen:</strong>
                <ul className="mb-0 mt-2 small">
                  <li>All users will lose access to this device</li>
                  <li>All pending access requests will be cancelled</li>
                  <li>All pending invitations will be cancelled</li>
                  <li>Device metadata will be cleared</li>
                  <li>The device will become &ldquo;unclaimed&rdquo;</li>
                  <li>The next user to register it will become the admin</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="reset-reason" className="form-label fw-medium">
              Reason for Reset <span className="text-danger">*</span>
            </label>
            <textarea
              id="reset-reason"
              className="form-control"
              rows="3"
              placeholder="Enter the reason for resetting device ownership (e.g., 'Device transfer', 'Employee departure', 'Ownership change')"
              value={state.resetModal.reason}
              onChange={(e) => setState(prev => ({ 
                ...prev, 
                resetModal: { ...prev.resetModal, reason: e.target.value },
              }))}
              disabled={state.resetModal.isLoading}
              required
            />
            <div className="form-text">
              This reason will be logged for audit purposes.
            </div>
          </div>

          <div className="alert alert-danger border-danger">
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
              <strong>This action cannot be undone!</strong>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleResetModalClose}
              disabled={state.resetModal.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleResetModalConfirm}
              disabled={state.resetModal.isLoading || !state.resetModal.reason.trim()}
            >
              {state.resetModal.isLoading ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Resetting Device...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Reset Device Ownership
                </>
              )}
            </button>
          </div>
        </div>
      </ModalPortal>

      {/* General Confirmation Modal */}
      {state.confirmModal.isOpen && (
        <ConfirmationModal
          isOpen={state.confirmModal.isOpen}
          onClose={handleConfirmModalClose}
          onConfirm={handleConfirmModalConfirm}
          isLoading={state.confirmModal.isLoading}
          {...getConfirmModalProps(state.confirmModal.type, state.confirmModal.data)}
        />
      )}
    </div>
  )
}

export default AccessManagement
